import { html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import type { ModalDialog } from '../ui/modal-dialog.js';
import type { ConfirmDialog } from '../ui/confirm-dialog.js';
import { dynamicsPenalty } from '../../dynamics-ranges.js';

// Dynamics tally as the history endpoint sends it (presses are stripped server-side).
interface PressLogTally {
    judged: number;
    outOfRange: number;
}

// Loop bounds carried by a test record (matches PersistedLoopRegion on the server).
interface PersistedLoopRegion {
    startMeasureIndex: number;
    endMeasureIndex: number;
    startTimeMs: number;
    endTimeMs: number;
    /** Display ticks (start inclusive, end exclusive). Absent on records saved before issue #9. */
    startTick?: number;
    endTick?: number;
}

/** A test-mode performance, as GET /api/history/:sliceId returns it. */
interface TestRecord {
    kind: 'test';
    id: number;
    playbackSpeed: number; // percent (100 = written tempo)
    score: number; // 0-100
    notesHit: number;
    totalNotes: number;
    longestCombo: number;
    pauseCount: number;
    loopRegion: PersistedLoopRegion | null;
    /** Null for records saved before press logging existed. */
    pressLog: PressLogTally | null;
    performedAt: string; // ISO string over the wire
}

/** A practice-mode pass, as GET /api/history/:sliceId returns it. */
interface PracticeRecord {
    kind: 'practice';
    id: number;
    scope: 'full' | 'loop';
    scopeStart: number | null;
    scopeEnd: number | null;
    playbackSpeed: number; // percent (150 = 1.5x)
    effectiveSpeed: number | null; // percent, nullable
    finalScore: number; // 0-100
    pressLog: PressLogTally | null;
    performedAt: string;
}

type HistoryRecord = TestRecord | PracticeRecord;

/**
 * Local calendar day of a timestamp, as YYYY-MM-DD. Local rather than UTC so a
 * late-evening session lands on the day the player remembers playing it.
 */
function dayKey(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** One day's records plus the figures the calendar cell shows. */
interface DaySummary {
    key: string;
    records: HistoryRecord[];
    testCount: number;
    practiceCount: number;
    /**
     * Lowest non-zero test score of the day — the weakest run you actually
     * played, which is where the work still is. A best-of-the-day hides that
     * behind one lucky pass. Zeros are dropped because they mark a run that
     * never got going rather than a bad one. Null when the day has no test
     * record with a score above zero; the cell then shows only its count.
     */
    worstTestScore: number | null;
}

/**
 * History calendar for the current slice. Groups both test-mode performances and
 * practice-mode passes by the day they were played, shows each day's activity on
 * a month grid, and drills into a day for the individual records.
 *
 * Replaces the two flat per-mode history lists: one slice can hold hundreds of
 * passes (56 in a single day in real data), which a single scrolling list cannot
 * make sense of.
 *
 * @fires performance-select - A test record was clicked, to recolor the score
 *   (detail: { performanceId: number, noteWindowsData: PersistedNoteWindowState[] })
 * @fires practice-performance-deleted - A practice record was deleted, so the host
 *   can refresh focus-area highlights derived from the same records.
 */
@customElement('history-calendar')
export class HistoryCalendar extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
            }

            :host(:not([visible])) {
                display: none;
            }

            :host([disabled]) .history-btn {
                opacity: 0.5;
                cursor: default;
                pointer-events: none;
            }

            .history-btn {
                background: var(--surface);
                border: 1px solid var(--border);
                border-radius: 4px;
                padding: 0.1rem 0.2rem;
                cursor: pointer;
                font-size: 1rem;
                transition: background 0.2s;
                display: flex;
                align-items: center;
                justify-content: center;
                color: inherit;
            }

            .history-btn:hover {
                background: var(--surface-light);
            }

            .history-icon {
                font-size: 1.7rem;
            }

            .loading,
            .no-history,
            .error {
                text-align: center;
                color: var(--text-muted);
                padding: 2rem;
            }

            .error {
                color: var(--error-color);
            }

            /* --- View header (month nav / day back link) --- */
            .view-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 0.5rem;
                margin-bottom: 0.75rem;
            }

            .month-label,
            .day-label {
                font-weight: 600;
                color: var(--text-primary);
            }

            .nav-btn {
                background: var(--surface);
                border: 1px solid var(--border);
                border-radius: 4px;
                color: var(--text-secondary);
                padding: 0.15rem 0.5rem;
                font-size: 1rem;
                line-height: 1.2;
                transition: background 0.2s, color 0.2s;
            }

            .nav-btn:hover:not(:disabled) {
                background: var(--surface-light);
                color: var(--text-primary);
            }

            .nav-btn:disabled {
                opacity: 0.35;
                cursor: default;
            }

            .back-btn {
                background: none;
                border: none;
                color: var(--primary-hover, #3b82f6);
                font-size: 0.875rem;
                padding: 0.15rem 0;
            }

            .back-btn:hover {
                text-decoration: underline;
            }

            /* --- Month grid --- */
            .weekdays,
            .day-grid {
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                gap: 4px;
            }

            .weekdays {
                margin-bottom: 4px;
            }

            .weekday {
                text-align: center;
                font-size: 0.7rem;
                color: var(--text-muted);
                text-transform: uppercase;
                letter-spacing: 0.04em;
            }

            .day-cell {
                position: relative;
                min-height: 62px;
                border: 1px solid var(--border);
                border-radius: 6px;
                padding: 0.2rem 0.25rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                background: var(--surface);
            }

            .day-cell.blank {
                border-color: transparent;
                background: none;
            }

            .day-cell.empty {
                opacity: 0.45;
            }

            .day-cell.active {
                cursor: pointer;
                transition: transform 0.1s, border-color 0.2s;
            }

            .day-cell.active:hover {
                border-color: var(--primary-hover, #3b82f6);
                transform: translateY(-1px);
            }

            .day-cell.today {
                border-color: var(--text-secondary);
            }

            .day-number {
                align-self: flex-start;
                font-size: 0.7rem;
                color: var(--text-muted);
                line-height: 1.2;
            }

            .day-best {
                font-size: 1.15rem;
                font-weight: bold;
                color: var(--primary-color);
                line-height: 1.2;
            }

            .day-count {
                font-size: 0.62rem;
                color: var(--text-muted);
            }

            .day-dots {
                position: absolute;
                top: 0.3rem;
                right: 0.3rem;
                display: flex;
                gap: 2px;
            }

            .dot {
                width: 6px;
                height: 6px;
                border-radius: 50%;
            }

            .dot.practice {
                background: var(--primary-hover, #3b82f6);
            }

            .dot.test {
                background: var(--warning-color, #f59e0b);
            }

            .legend {
                display: flex;
                gap: 1rem;
                justify-content: center;
                margin-top: 0.75rem;
                font-size: 0.7rem;
                color: var(--text-muted);
            }

            .legend span {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
            }

            /* --- Day detail list (same rows as the old history modals) --- */
            .day-summary {
                font-size: 0.8rem;
                color: var(--text-muted);
                margin-bottom: 0.75rem;
            }

            .history-list {
                max-height: 55vh;
                overflow-y: auto;
                padding-right: 0.5rem;
            }

            .history-item {
                display: flex;
                align-items: center;
                gap: 1rem;
                padding: 0.75rem 1rem;
                border: 1px solid var(--border);
                border-radius: 8px;
                margin-bottom: 0.75rem;
                transition: background 0.2s, transform 0.1s;
            }

            .history-item.clickable {
                cursor: pointer;
            }

            .history-item.clickable:hover {
                background: var(--surface-light);
                transform: translateX(4px);
            }

            .history-score {
                font-size: 1.5rem;
                font-weight: bold;
                min-width: 50px;
                color: var(--primary-color);
                text-align: center;
            }

            .history-details {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                font-size: 0.875rem;
                color: var(--text-secondary);
            }

            .history-time {
                color: var(--text-primary);
                font-weight: 500;
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
            }

            .kind-tag {
                font-size: 0.65rem;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                border-radius: 4px;
                padding: 0.05rem 0.3rem;
                font-weight: 600;
            }

            .kind-tag.practice {
                color: var(--primary-hover, #3b82f6);
                border: 1px solid var(--primary-hover, #3b82f6);
            }

            .kind-tag.test {
                color: var(--warning-color, #f59e0b);
                border: 1px solid var(--warning-color, #f59e0b);
            }

            .history-speed {
                color: var(--text-secondary);
            }

            .history-stats,
            .dynamics-stats {
                color: var(--text-muted);
                font-size: 0.8rem;
            }

            .dynamics-stats .penalty {
                color: var(--warning-color, #f59e0b);
            }

            .loop-badge {
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
                font-size: 0.75rem;
                color: var(--primary-color);
                background: rgba(var(--primary-rgb, 59, 130, 246), 0.1);
                border: 1px solid rgba(var(--primary-rgb, 59, 130, 246), 0.3);
                border-radius: 12px;
                padding: 0.1rem 0.5rem;
                white-space: nowrap;
                align-self: flex-start;
            }

            .delete-btn {
                background: none;
                border: none;
                cursor: pointer;
                color: var(--text-muted);
                font-size: 1.1rem;
                padding: 0.25rem;
                border-radius: 4px;
                transition: color 0.2s, background 0.2s;
                margin-left: auto;
                flex-shrink: 0;
            }

            .delete-btn:hover {
                color: var(--error-color, #ef4444);
                background: rgba(239, 68, 68, 0.1);
            }
        `,
    ];

    @property({ type: String, attribute: 'slice-id' })
    sliceId = '';

    @property({ type: String, attribute: 'auth-token' })
    authToken = '';

    @property({ type: Boolean, reflect: true })
    visible = false;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    @state()
    private _records: HistoryRecord[] = [];

    @state()
    private _loading = false;

    @state()
    private _error = '';

    /** First of the month currently on screen. */
    @state()
    private _viewMonth: Date = new Date();

    /** Day key of the open detail view; null while the month grid is showing. */
    @state()
    private _selectedDay: string | null = null;

    @state()
    private _deleting: HistoryRecord | null = null;

    @state()
    private _deleteLoading = false;

    @state()
    private _deleteError = '';

    @query('modal-dialog')
    private _modal!: ModalDialog;

    @query('confirm-dialog')
    private _confirmDialog!: ConfirmDialog;

    /** Open the modal on the month grid and load this slice's history. */
    async show(): Promise<void> {
        if (this.disabled) return;
        if (!this.sliceId || !this.authToken) {
            console.error('[HistoryCalendar] Cannot show - missing sliceId or authToken');
            return;
        }

        this._loading = true;
        this._error = '';
        this._records = [];
        this._selectedDay = null;
        this._modal?.show();

        try {
            const response = await fetch(`/museiq/api/history/${this.sliceId}`, {
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch history');
            }

            const data = await response.json();
            const test: TestRecord[] = (data.test ?? []).map((r: Omit<TestRecord, 'kind'>) => ({
                ...r,
                kind: 'test' as const,
            }));
            const practice: PracticeRecord[] = (data.practice ?? []).map(
                (r: Omit<PracticeRecord, 'kind'>) => ({ ...r, kind: 'practice' as const })
            );
            this._records = [...test, ...practice].sort(
                (a, b) => new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()
            );

            // Open on the month of the newest record so there is something to see.
            const newest = this._records[0];
            const anchor = newest ? new Date(newest.performedAt) : new Date();
            this._viewMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        } catch (error) {
            console.error('[HistoryCalendar] Error loading history:', error);
            this._error = 'Failed to load history.';
        } finally {
            this._loading = false;
        }
    }

    /** Close the modal. */
    hide(): void {
        this._modal?.hide();
    }

    /** Records grouped by local day, with each day's cell figures. */
    private _daySummaries(): Map<string, DaySummary> {
        const days = new Map<string, DaySummary>();
        for (const record of this._records) {
            const key = dayKey(new Date(record.performedAt));
            let day = days.get(key);
            if (!day) {
                day = { key, records: [], testCount: 0, practiceCount: 0, worstTestScore: null };
                days.set(key, day);
            }
            day.records.push(record);
            if (record.kind === 'test') {
                day.testCount++;
                if (record.score > 0) {
                    day.worstTestScore =
                        day.worstTestScore === null
                            ? record.score
                            : Math.min(day.worstTestScore, record.score);
                }
            } else {
                day.practiceCount++;
            }
        }
        return days;
    }

    /** Months with records, as first-of-month timestamps, oldest first. */
    private _monthBounds(): { first: Date; last: Date } {
        const now = new Date();
        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        if (this._records.length === 0) return { first: thisMonth, last: thisMonth };

        // _records is newest-first, so the ends give the range directly.
        const newest = new Date(this._records[0].performedAt);
        const oldest = new Date(this._records[this._records.length - 1].performedAt);
        const first = new Date(oldest.getFullYear(), oldest.getMonth(), 1);
        const newestMonth = new Date(newest.getFullYear(), newest.getMonth(), 1);
        // Let the player page forward to the current month even if it is still empty.
        const last = newestMonth > thisMonth ? newestMonth : thisMonth;
        return { first, last };
    }

    private _shiftMonth(delta: number): void {
        this._viewMonth = new Date(
            this._viewMonth.getFullYear(),
            this._viewMonth.getMonth() + delta,
            1
        );
    }

    private _openDay(key: string): void {
        this._selectedDay = key;
    }

    private _backToMonth(): void {
        this._selectedDay = null;
    }

    /** Load full note data for a test record and hand it to the host to recolor. */
    private async _selectTestRecord(id: number): Promise<void> {
        try {
            const response = await fetch(`/museiq/api/performances/detail/${id}`, {
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch performance details');
            }

            const data = await response.json();

            this.dispatchEvent(
                new CustomEvent('performance-select', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        performanceId: id,
                        noteWindowsData: data.performance.noteWindowsData,
                    },
                })
            );

            this._modal?.hide();
            console.log(`[HistoryCalendar] Applied colors from performance ${id}`);
        } catch (error) {
            console.error('[HistoryCalendar] Error loading performance details:', error);
        }
    }

    private _confirmDelete(record: HistoryRecord, e: Event): void {
        e.stopPropagation();
        this._deleting = record;
        this._deleteError = '';
        this._deleteLoading = false;
        this._confirmDialog?.show();
    }

    private async _deleteRecord(): Promise<void> {
        const record = this._deleting;
        if (!record) return;

        this._deleteLoading = true;
        this._deleteError = '';

        const path = record.kind === 'test' ? 'performances' : 'practice-performances';

        try {
            const response = await fetch(`/museiq/api/${path}/${record.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete record');
            }

            this._records = this._records.filter(
                (r) => !(r.kind === record.kind && r.id === record.id)
            );
            this._deleting = null;
            this._confirmDialog?.hide();

            // Nothing left on this day — the detail view would be empty.
            if (this._selectedDay && !this._daySummaries().has(this._selectedDay)) {
                this._selectedDay = null;
            }

            // Practice records feed the focus-area highlights; let the host refresh.
            if (record.kind === 'practice') {
                this.dispatchEvent(
                    new CustomEvent('practice-performance-deleted', {
                        bubbles: true,
                        composed: true,
                    })
                );
            }
        } catch (error) {
            console.error('[HistoryCalendar] Error deleting record:', error);
            this._deleteError = 'Failed to delete. Please try again.';
        } finally {
            this._deleteLoading = false;
        }
    }

    private _cancelDelete(): void {
        this._deleting = null;
        this._deleteError = '';
        this._deleteLoading = false;
    }

    /**
     * Dynamics line for one record. Renders nothing when no onset was judged —
     * dynamics feedback off, slice without markings, or a pass that was entirely
     * hairpin-exempt. That is also why older records (null pressLog) stay clean.
     */
    private _dynamicsSummary(record: HistoryRecord) {
        const log = record.pressLog;
        if (!log || log.judged <= 0) return nothing;
        const inRangePct = Math.round(((log.judged - log.outOfRange) / log.judged) * 100);
        const penalty = dynamicsPenalty(log.judged, log.outOfRange);
        return html`
            <span class="dynamics-stats"
                >Dynamics ${inRangePct}% in range${penalty > 0
                    ? html` <span class="penalty">&minus;${penalty} pts</span>`
                    : nothing}</span
            >
        `;
    }

    private _formatTime(iso: string): string {
        return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    // ---------- Month view ----------

    private _renderMonth(days: Map<string, DaySummary>) {
        const year = this._viewMonth.getFullYear();
        const month = this._viewMonth.getMonth();
        const leadingBlanks = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cellCount = Math.ceil((leadingBlanks + daysInMonth) / 7) * 7;
        const todayKey = dayKey(new Date());

        // Busiest day in the whole history sets the shading scale, so the tint
        // means the same thing as the player pages between months.
        const busiest = Math.max(1, ...[...days.values()].map((d) => d.records.length));

        const { first, last } = this._monthBounds();
        const atFirst = year === first.getFullYear() && month === first.getMonth();
        const atLast = year === last.getFullYear() && month === last.getMonth();

        const cells = [];
        for (let i = 0; i < cellCount; i++) {
            const dayNumber = i - leadingBlanks + 1;
            if (dayNumber < 1 || dayNumber > daysInMonth) {
                cells.push(html`<div class="day-cell blank"></div>`);
                continue;
            }
            const key = dayKey(new Date(year, month, dayNumber));
            const day = days.get(key);
            const classes = [
                'day-cell',
                day ? 'active' : 'empty',
                key === todayKey ? 'today' : '',
            ]
                .filter(Boolean)
                .join(' ');

            if (!day) {
                cells.push(html`
                    <div class=${classes}><span class="day-number">${dayNumber}</span></div>
                `);
                continue;
            }

            const total = day.records.length;
            // 0.08–0.30 alpha: visible as a density cue without fighting the text.
            const shade = 0.08 + 0.22 * Math.min(1, total / busiest);
            const runs = `${total} ${total === 1 ? 'run' : 'runs'}`;
            const title =
                day.worstTestScore !== null
                    ? `${runs} · worst test ${day.worstTestScore}`
                    : runs;
            cells.push(html`
                <div
                    class=${classes}
                    style="background: rgba(37, 99, 235, ${shade.toFixed(2)})"
                    title=${title}
                    @click=${() => this._openDay(key)}
                >
                    <span class="day-number">${dayNumber}</span>
                    <span class="day-dots">
                        ${day.practiceCount > 0
                            ? html`<span class="dot practice"></span>`
                            : nothing}
                        ${day.testCount > 0 ? html`<span class="dot test"></span>` : nothing}
                    </span>
                    ${day.worstTestScore !== null
                        ? html`<span class="day-best">${day.worstTestScore}</span>`
                        : nothing}
                    <span class="day-count">${runs}</span>
                </div>
            `);
        }

        return html`
            <div class="view-header">
                <button
                    class="nav-btn"
                    title="Previous month"
                    ?disabled=${atFirst}
                    @click=${() => this._shiftMonth(-1)}
                >
                    &lsaquo;
                </button>
                <span class="month-label"
                    >${this._viewMonth.toLocaleDateString([], {
                        month: 'long',
                        year: 'numeric',
                    })}</span
                >
                <button
                    class="nav-btn"
                    title="Next month"
                    ?disabled=${atLast}
                    @click=${() => this._shiftMonth(1)}
                >
                    &rsaquo;
                </button>
            </div>
            <div class="weekdays">
                ${WEEKDAYS.map((d) => html`<span class="weekday">${d}</span>`)}
            </div>
            <div class="day-grid">${cells}</div>
            <div class="legend">
                <span><span class="dot practice"></span> practice</span>
                <span><span class="dot test"></span> test</span>
                <span>score = worst test run of the day</span>
            </div>
        `;
    }

    // ---------- Day view ----------

    private _renderTestRow(r: TestRecord) {
        return html`
            <div class="history-item clickable" @click=${() => this._selectTestRecord(r.id)}>
                <div class="history-score">${r.score}</div>
                <div class="history-details">
                    <span class="history-time"
                        >${this._formatTime(r.performedAt)}
                        <span class="kind-tag test">Test</span></span
                    >
                    <span class="history-speed">${r.playbackSpeed}% speed</span>
                    <span class="history-stats"
                        >${r.notesHit}/${r.totalNotes} notes | ${r.longestCombo} combo |
                        ${r.pauseCount ?? 0} pauses</span
                    >
                    ${this._dynamicsSummary(r)}
                    ${r.loopRegion
                        ? html`<span class="loop-badge"
                              >&#128257; Measures ${r.loopRegion.startMeasureIndex + 1}–${r
                                  .loopRegion.endMeasureIndex + 1}</span
                          >`
                        : nothing}
                </div>
                <button
                    class="delete-btn"
                    title="Delete performance"
                    @click=${(e: Event) => this._confirmDelete(r, e)}
                >
                    &times;
                </button>
            </div>
        `;
    }

    private _renderPracticeRow(r: PracticeRecord) {
        const scope =
            r.scope === 'loop' && r.scopeStart !== null && r.scopeEnd !== null
                ? html`<span class="loop-badge"
                      >&#128257; Measures ${r.scopeStart + 1}–${r.scopeEnd + 1}</span
                  >`
                : html`<span class="loop-badge">Full run</span>`;

        return html`
            <div class="history-item">
                <div class="history-score">${r.finalScore}</div>
                <div class="history-details">
                    <span class="history-time"
                        >${this._formatTime(r.performedAt)}
                        <span class="kind-tag practice">Practice</span></span
                    >
                    <span class="history-speed"
                        >${r.playbackSpeed}% speed${r.effectiveSpeed != null
                            ? html` · ~${r.effectiveSpeed}% effective`
                            : nothing}</span
                    >
                    ${this._dynamicsSummary(r)} ${scope}
                </div>
                <button
                    class="delete-btn"
                    title="Delete practice record"
                    @click=${(e: Event) => this._confirmDelete(r, e)}
                >
                    &times;
                </button>
            </div>
        `;
    }

    private _renderDay(day: DaySummary) {
        // Same figure the calendar cell shows, so the drill-down explains the cell.
        const parts: string[] = [];
        if (day.practiceCount > 0) parts.push(`${day.practiceCount} practice`);
        if (day.testCount > 0) parts.push(`${day.testCount} test`);
        if (day.worstTestScore !== null) parts.push(`worst test ${day.worstTestScore}`);

        // Records arrive newest-first; a day reads better oldest-first.
        const ordered = [...day.records].reverse();
        const date = new Date(day.records[0].performedAt);

        return html`
            <div class="view-header">
                <button class="back-btn" @click=${this._backToMonth}>&lsaquo; All months</button>
                <span class="day-label"
                    >${date.toLocaleDateString([], {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                    })}</span
                >
            </div>
            <div class="day-summary">${parts.join(' · ')}</div>
            <div class="history-list">
                ${ordered.map((r) =>
                    r.kind === 'test' ? this._renderTestRow(r) : this._renderPracticeRow(r)
                )}
            </div>
        `;
    }

    private _renderContent() {
        if (this._loading) {
            return html`<p class="loading">Loading...</p>`;
        }
        if (this._error) {
            return html`<p class="error">${this._error}</p>`;
        }
        if (this._records.length === 0) {
            return html`<p class="no-history">No practice or test records yet.</p>`;
        }

        const days = this._daySummaries();
        const selected = this._selectedDay ? days.get(this._selectedDay) : undefined;
        return selected ? this._renderDay(selected) : this._renderMonth(days);
    }

    render() {
        const deletingPractice = this._deleting?.kind === 'practice';
        return html`
            <button class="history-btn" @click=${this.show} title="View History">
                <span class="history-icon">&#128337;</span>
            </button>
            <modal-dialog heading="History" size="medium">
                ${this._renderContent()}
            </modal-dialog>
            <confirm-dialog
                heading=${deletingPractice ? 'Delete Practice Record' : 'Delete Performance'}
                message=${deletingPractice
                    ? 'Are you sure you want to delete this practice record?'
                    : 'Are you sure you want to delete this performance record?'}
                confirm-label="Delete"
                confirm-variant="danger"
                .loading=${this._deleteLoading}
                .error=${this._deleteError}
                @confirm=${this._deleteRecord}
                @dialog-cancel=${this._cancelDelete}
            ></confirm-dialog>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'history-calendar': HistoryCalendar;
    }
}
